import React from 'react'
import {ClipboardList, Users, Wallet} from 'lucide-react'
import * as S from './styles/authShell.styles'
import StaffologyMark from './StaffologyMark'

const JOURNEY = [
  {short: 'CO', label: 'Company'},
  {short: 'MSP', label: 'MSP'},
  {short: 'AG', label: 'Agency'},
  {short: 'WK', label: 'Worker'},
]

function AuthBrand() {
  return (
    <S.Brand>
      <StaffologyMark />
      <span>
        Staffology
        <small>Workforce platform</small>
      </span>
    </S.Brand>
  )
}

function AuthIntro() {
  return (
    <S.Intro aria-label="About Staffology" data-testid="auth-intro">
      <AuthBrand />
      <S.Headline>
        Every order, worker and payroll, <span>in one place.</span>
      </S.Headline>
      <S.Story>
        Staffology connects companies, MSPs, agencies and workers, from the first staffing request
        to the final paycheck.
      </S.Story>
      <S.Journey aria-hidden="true">
        <S.JourneyHead>
          <span>How work moves</span>
          <b>Order to assignment</b>
        </S.JourneyHead>
        <S.Track>
          <S.Beam />
          {JOURNEY.map((step, index) => (
            <S.Node key={step.label} $index={index}>
              <i>{step.short}</i>
              <span>{step.label}</span>
            </S.Node>
          ))}
        </S.Track>
      </S.Journey>
      <S.Features>
        <li>
          <ClipboardList aria-hidden="true" />
          <strong>Orders</strong>Service orders, pledges and fulfillment.
        </li>
        <li>
          <Users aria-hidden="true" />
          <strong>Workers</strong>Candidates, compliance and assignments.
        </li>
        <li>
          <Wallet aria-hidden="true" />
          <strong>Time and pay</strong>Timesheets, invoices and payroll.
        </li>
      </S.Features>
    </S.Intro>
  )
}

export interface AuthShellProps {
  children: React.ReactNode
  below?: React.ReactNode
  footer?: React.ReactNode
  variant?: 'split' | 'centered'
  'data-testid'?: string
  cardTestId?: string
}

export default function AuthShell({
  children,
  below,
  footer,
  variant = 'split',
  'data-testid': testId,
  cardTestId,
}: Readonly<AuthShellProps>) {
  return (
    <S.Scene data-testid={testId}>
      <S.World aria-hidden="true">
        <S.Photo $paris={Boolean(import.meta.env.VITE_FR)} />
        <S.Wash $flat={variant === 'centered'} />
        <S.Aurora $variant="a" />
        <S.Aurora $variant="b" />
        <S.Aurora $variant="c" />
      </S.World>
      <S.Stage $centered={variant === 'centered'}>
        {variant === 'centered' ? (
          <S.CenteredBrand>
            <AuthBrand />
          </S.CenteredBrand>
        ) : (
          <AuthIntro />
        )}
        <S.AuthColumn>
          <S.Card data-auth-card="" aria-live="polite" data-testid={cardTestId}>
            {children}
          </S.Card>
          {below ? <S.Below>{below}</S.Below> : null}
        </S.AuthColumn>
      </S.Stage>
      {footer}
      <S.Iris data-auth-iris="" aria-hidden="true" />
    </S.Scene>
  )
}
